import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";

import { api } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import DecisionSystemVisual from "@/components/DecisionSystemVisual";
import DecisionNetworkGraphic from "@/components/DecisionNetworkGraphic";
import PageHero from "@/components/PageHero";
import { SectionBand, SectionHeader, ArrowLink } from "@/components/SectionPrimitives";
import { CAPABILITIES } from "@/lib/dataleo";
import { articlePath } from "@/lib/radarPaths";


// Filter out obvious placeholder / test seed content.
const isRealArticle = (n) => {
  const t = (n?.title || "").toLowerCase();
  const bad = ["test from gpt", "test -", "test_", "e2e ", "batch ", "fresh badge", "seo test", "realtime ", "ingestion d"];
  return !!t && !bad.some((b) => t.includes(b)) && !(n?.url || "").includes("example.com");
};

const CorporateHome = () => {
  const { lang } = useLang();

  const [radarNews, setRadarNews] = useState([]);


  useEffect(() => {
    api.get(`/news?language=${lang}&limit=8`)
      .then((r) => setRadarNews((r.data || []).filter(isRealArticle).slice(0, 4)))
      .catch(() => {});
  }, [lang]);

  const t = lang === "fr" ? {
    heroKicker: "Boutique Supply Chain · Decision Systems",
    hero: "De meilleures décisions Supply Chain. Des résultats concrets.",
    heroSub: "Nous accompagnons les directions Supply Chain, Opérations et IT : transformation de la planification, systèmes de décision d’entreprise et IA appliquée.",
    primaryCta: "Nos capacités",
    secondaryCta: "Nous contacter",
    readyKicker: "Êtes-vous prêt ?",
    readyTitle: "Deux sujets qui changent la donne cette année.",
    tileIa:   { label: "IA",         title: "Agents IA & Decision Build : du prototype au système de confiance." },
    tileGov:  { label: "RUN",        title: "Gouvernance & TMA : garder un système de décision vivant." },
    newsKicker: "À la une",
    newsTitle: "Les dernières analyses du Radar.",
    newsMore: "Ouvrir le Radar",
    expertiseKicker: "Nos capacités",
    expertiseTitle: "Trois leviers métiers.\nUn système de décision.",
    expertiseIntro: "Nous combinons trois capacités complémentaires - mobilisées séparément ou en système - pour concevoir, livrer et faire vivre vos décisions Supply Chain.",
    expertiseMore: "Toutes les capacités",
    proofKicker: "Ils nous font confiance",
    proofTitle: "Transformations menées côté client.",
    proofMore: "Toutes nos références",
    numbersKicker: "Dataleo en chiffres",
    numbersTitle: "Un partenaire reconnu par ses clients.",
  } : {
    heroKicker: "Supply Chain Decision Systems boutique",
    hero: "Better Supply Chain Decisions. Built to Deliver.",
    heroSub: "Senior-led advisory for Supply Chain, Operations and IT leaders: planning transformation, enterprise decision systems and practical AI.",
    primaryCta: "Our capabilities",
    secondaryCta: "Talk to Dataleo",
    readyKicker: "Are you ready?",
    readyTitle: "Two topics that will shift the needle this year.",
    tileIa:   { label: "AI",         title: "AI agents & Decision Build: from prototype to trusted system." },
    tileGov:  { label: "RUN",        title: "Governance & AMS: keep a decision system alive after go-live." },
    newsKicker: "In the spotlight",
    newsTitle: "Latest intelligence from Radar.",
    newsMore: "Open the Radar",
    expertiseKicker: "Our capabilities",
    expertiseTitle: "Three business levers.\nOne decision system.",
    expertiseIntro: "We combine three complementary capabilities - mobilised separately or as one system - to design, deliver and operate your Supply Chain decisions.",
    expertiseMore: "All capabilities",
    proofKicker: "Trusted by",
    proofTitle: "Transformations led client-side.",
    proofMore: "All references",
    numbersKicker: "Dataleo in numbers",
    numbersTitle: "A partner recognised by its clients.",
  };

  return (
    <div data-testid="corporate-home">
      <SeoHead
        lang={lang}
        path="/"
        bareTitle
        title={lang === "fr" ? "Dataleo - Supply Chain Decision Systems" : "Dataleo - Supply Chain Decision Systems"}
        description={t.heroSub}
      />

      {/* 1 · HERO - cream background, giant Archivo Black title, pill CTAs,
          right-side hero photo (strategy room). */}
      <PageHero
        tone="cream"
        kicker={t.heroKicker}
        title={t.hero}
        description={t.heroSub}
        primaryCta={{ to: "/capabilities", label: t.primaryCta }}
        secondaryCta={{ to: "/contact", label: t.secondaryCta }}
        visual={<DecisionNetworkGraphic />}

        testId="home-hero"
      />

      <SectionBand tone="cream" id="home-challenges" testId="home-challenges">
        <SectionHeader
          kicker={lang === "fr" ? "Vos enjeux" : "Your challenges"}
          title={lang === "fr" ? "La technologie seule ne résout pas le problème." : "Technology alone is not the answer."}
          description={lang === "fr" ? "Nous intervenons à la croisée des processus, des systèmes et des responsabilités de décision." : "We work where processes, enterprise systems and decision accountability intersect."}
        />
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          {[
            { en: "Planning still runs in spreadsheets", fr: "La planification repose encore sur Excel", be: "Disconnected data, manual reconciliation and inconsistent assumptions.", bf: "Données cloisonnées, rapprochements manuels et hypothèses divergentes.", to: "/capabilities/advisory" },
            { en: "Enterprise planning programs lose momentum", fr: "Les programmes APS s'essoufflent", be: "Integration complexity, unclear ownership and difficult deployment.", bf: "Intégrations complexes, responsabilités floues et déploiements difficiles.", to: "/capabilities/delivery" },
            { en: "Operational decisions remain fragmented", fr: "Les décisions opérationnelles restent fragmentées", be: "Functions need shared governance and clear decision rights.", bf: "Les fonctions ont besoin d'une gouvernance commune et de responsabilités claires.", to: "/capabilities/advisory" },
            { en: "AI prototypes fail to reach operations", fr: "Les prototypes IA ne passent pas en production", be: "Usable data, controls and human accountability matter more than demos.", bf: "La donnée, les contrôles et la responsabilité humaine comptent davantage qu'une démo.", to: "/capabilities/ai-build" },
          ].map((item, i) => (
            <Link key={item.en} to={item.to} className="group rounded-2xl bg-white border border-[#E6E0D5] hover:border-[#00A878] transition-colors p-7">
              <span className="font-mono-dl text-xs text-[#00A878]">0{i + 1}</span>
              <h3 className="font-display text-2xl sm:text-3xl mt-4 text-[#0B1210]">{lang === "fr" ? item.fr : item.en}</h3>
              <p className="text-[#4B5563] text-sm leading-relaxed mt-3">{lang === "fr" ? item.bf : item.be}</p>
              <span className="inline-flex gap-2 mt-5 text-sm font-bold-dl text-[#08785D]">{lang === "fr" ? "Notre approche" : "How we help"} <ArrowRight size={15} /></span>
            </Link>
          ))}
        </div>
      </SectionBand>
      <SectionBand tone="dark" testId="home-decision-system">
        <SectionHeader tone="dark" kicker={lang === "fr" ? "Notre conviction" : "Our conviction"}
          title={lang === "fr" ? "De la donnée à la décision." : "From data to decisions."}
          description={lang === "fr" ? "Un système de décision relie signaux, règles, outils et action humaine." : "Decision systems connect signals, rules, technology and accountable action."}
        />
        <div className="mt-10"><DecisionSystemVisual /></div>
      </SectionBand>
      {/* 3 · NEWS - À la une, radar signals as big-type list. */}
      <SectionBand tone="cream" id="home-news" testId="home-news">
        <SectionHeader kicker={t.newsKicker} title={t.newsTitle} />
        <ul className="mt-16 divide-y divide-[#E6E0D5] border-y border-[#E6E0D5]">
          {radarNews.length === 0 ? (
            <li className="py-10 text-[#4B5563] text-[14px]">{lang === "fr" ? "Retrouvez nos analyses dans le Radar." : "Explore our insights in Radar."}</li>
          ) : radarNews.map((n) => (
            <li key={n.id}>
              <Link
                to={articlePath("news", n)}
                data-testid={`home-signal-${n.id}`}
                className="group grid grid-cols-[90px_1fr_auto] sm:grid-cols-[120px_1fr_auto] items-center gap-4 sm:gap-8 py-6 sm:py-8 hover:bg-white transition-colors -mx-4 px-4 rounded-xl"
              >
                <span className="font-mono-dl text-[11px] tracking-widest text-[#4B5563]">
                  {n.published_at?.slice(0, 10)}
                </span>
                <h3 className="font-display text-[22px] sm:text-[28px] text-[#0B1210] leading-[1.1] group-hover:text-[#00A878] transition-colors">
                  {n.title}
                </h3>
                <ArrowRight
                  size={22}
                  strokeWidth={2.5}
                  className="text-[#4B5563] group-hover:text-[#00A878] group-hover:translate-x-1 transition-all shrink-0"
                />
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-10">
          <ArrowLink to="/radar" testId="home-signals-more">{t.newsMore}</ArrowLink>
        </div>
      </SectionBand>

      {/* 4 · EXPERTISES - dark band with the 3 capabilities as big rows. */}
      <SectionBand tone="dark" id="home-expertise" testId="home-expertise">
        <SectionHeader
          kicker={t.expertiseKicker}
          title={<span style={{ whiteSpace: "pre-line" }}>{t.expertiseTitle}</span>}
          description={t.expertiseIntro}
          tone="dark"
        />
        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {CAPABILITIES.map((p, i) => (
            <Link
              key={p.slug}
              to={`/capabilities/${p.slug}`}
              data-testid={`home-capability-${p.slug}`}
              className="group block bg-white text-[#0B1210] rounded-[28px] p-8 min-h-[320px] flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            >
              <div>
                <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                  0{i + 1} · {p.tag}
                </div>
                <h3 className="font-display text-3xl sm:text-4xl mt-4 leading-[1.0]">
                  {lang === "fr" ? p.title.fr : p.title.en}
                </h3>
                <p className="mt-4 text-[14px] text-[#4B5563] leading-relaxed">
                  {lang === "fr" ? p.tagline.fr : p.tagline.en}
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-bold-dl text-[#0B1210] group-hover:text-[#00A878] transition-colors">
                <span className="border-b border-current pb-0.5">
                  {lang === "fr" ? "Explorer" : "Explore"}
                </span>
                <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-10">
          <ArrowLink to="/capabilities" tone="light" testId="home-expertise-more">{t.expertiseMore}</ArrowLink>
        </div>
      </SectionBand>

      <SectionBand tone="cream" testId="home-founder">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-10 items-start">
          <div><p className="font-mono-dl text-[11px] tracking-[0.2em] uppercase text-[#00A878]">{lang === "fr" ? "Une expertise engagée" : "Senior-led expertise"}</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-4">{lang === "fr" ? "Une boutique, un engagement direct." : "Independent expertise. Direct accountability."}</h2></div>
          <div><h3 className="font-display text-2xl">Jean-François Nordmann</h3>
            <p className="mt-4 text-[#4B5563] leading-relaxed">{lang === "fr" ? "Fondateur de DataLeo, spécialisé dans les transformations Supply Chain et les systèmes de planification. Une approche pragmatique à l'interface du métier, de l'IT et des éditeurs." : "DataLeo founder, focused on Supply Chain transformation and enterprise planning systems. A pragmatic approach across business teams, IT and technology providers."}</p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-6 font-bold-dl text-[#08785D] hover:underline">{lang === "fr" ? "Découvrir notre approche" : "Learn about our approach"} <ArrowRight size={16}/></Link>
          </div>
        </div>
      </SectionBand>
    </div>
  );
};

export default CorporateHome;
